/**
 * Card ↔ Notes cleanup — manages flashcard marks in Tiptap notes documents.
 *
 * When cards are deleted / restored, the corresponding marks in the source
 * notes document need to be cleaned up / restored. This module handles
 * that side effect independently of any React hook.
 *
 * Storage: the notes are in the per-user Yjs doc (see `src/sync/yjs/`).
 * Reads go straight through the `sync.decks.deck(id).notes.getJSON()`
 * facade; writes have to go through a live Tiptap editor because that's
 * where the ProseMirror schema needed for `prosemirrorJSONToYXmlFragment`
 * lives. `notes-editor-registry.ts` tracks the mounted editor per note
 * and queues writes when none is mounted — see that file for the flow.
 *
 * ── A cleanup is an INTENT, never a document ───────────────────────────
 * Both writes below go through `updateNotesContent` with a transform that is
 * evaluated against the note's LIVE document at the moment it is applied —
 * which, for a card deleted from the flashcards view with no notes editor
 * open, is whenever the note is next opened. Reading the note now, computing
 * the cleaned document and writing THAT later would replace whatever the
 * note holds by then (a paragraph typed on the phone in between) with a
 * stale copy, on every device. So: the removal strips these ids from
 * whatever the note contains when it lands; the undo re-adds these ids where
 * the text still matches. Neither ever writes text.
 */

import type { SyncCardProps as CardProps } from '@flashka/shared/sync-engine/schema/shared-types';
import { asDeckId } from '@flashka/shared/sync-engine/schema/shared-types';

import type { JSONContent } from './internal/editor-types';
import {
  addFlashcardMarksFromSnapshot,
  collectFlashcardMarkIdsFromJSON,
  removeFlashcardMarksFromJSON,
} from './internal/utils/flashcard-marks';
import { parseNotesSource } from './internal/utils/notes-source';
import { updateNotesContent } from './notes-editor-registry';
import { emitNotesMarksRestored } from './notes-marks-events';
import { sync } from './sync';

export const NOTES_MARKS_RESTORED_EVENT = 'notes-marks-restored';

/**
 * Where each deleted card's mark WAS, so an undo can put it back. A snapshot
 * is only ever consulted for mark positions (`addFlashcardMarksFromSnapshot`
 * copies marks onto text that still matches) — its text is never written.
 */
const markUndoSnapshots = new Map<
  string,
  { docId: string; noteId: string; content: JSONContent }
>();

/**
 * Read the current JSON of ONE note, if it exists.
 *
 * The note id comes off the card's own ref. Reading the deck's first note
 * instead — which is all this could do before refs carried an id — meant
 * deleting a card made from Note 3 stripped highlights out of Note 1: marks
 * the user never touched, in a document they were not looking at.
 */
function readNotesJSON(docId: string, noteId: string): JSONContent | undefined {
  return sync.decks.deck(asDeckId(docId)).notes.getJSON(noteId);
}

/** Cards are grouped by the NOTE they came from, not by the deck. */
const groupKey = (docId: string, noteId: string) => `${docId}\x01${noteId}`;
const splitGroupKey = (key: string): { docId: string; noteId: string } => {
  const at = key.lastIndexOf('\x01');
  return { docId: key.slice(0, at), noteId: key.slice(at + 1) };
};

/**
 * Clean up flashcard marks in notes documents for deleted cards.
 * Saves undo snapshots so marks can be restored on undo.
 * Fire-and-forget — callers should NOT await this.
 *
 * ── This is a NO-OP on mobile, and says so out loud ────────────────────
 * Both halves of the mechanism are web-shaped: the read goes through
 * `sync.decks…notes`, and the write needs a Tiptap schema via
 * `notes-editor-registry`. flashka-app edits its notes inside an Expo DOM
 * WebView, so the Y.Doc and the schema both live there and neither is reachable
 * from the RN runtime — this function finds nothing and returns. That silence
 * is how a deleted card kept its highlight on mobile for as long as it did, so
 * the miss is now logged rather than swallowed. Mobile does the same
 * reconciliation over the DOM bridge instead (`useOrphanFlashcardMarks` +
 * the editor's `orphanFlashcardIds` / `onFlashcardMarkIds`), as a surgical
 * mark transaction on the live document — the same "never replace the text"
 * rule this module follows; any NEW caller that deletes cards is cleaned up
 * on web and must check that a mobile path exists before assuming this
 * covered it.
 */
export async function cleanupNotesMarks(
  deletedCards: CardProps[],
  saveForUndo = true,
): Promise<void> {
  const markIdsByDoc = new Map<string, string[]>();
  for (const card of deletedCards) {
    const parsed = parseNotesSource(card.source ?? '');
    if (!parsed) continue;
    const key = groupKey(parsed.docId, parsed.noteId);
    const ids = markIdsByDoc.get(key) ?? [];
    ids.push(card.id);
    markIdsByDoc.set(key, ids);
  }
  if (markIdsByDoc.size === 0) return;

  for (const [key, cardIds] of markIdsByDoc) {
    const { docId, noteId } = splitGroupKey(key);
    const current = readNotesJSON(docId, noteId);
    if (!current) {
      // The cards claimed a `notes:` source, so a note is supposed to exist —
      // reaching here means we could not see it (no NoteDoc held, or no runtime
      // that can hold one) and those highlights are being left behind.
      console.warn(
        '[card-notes-cleanup] no readable note for doc; leaving flashcard marks in place',
        { docId, noteId, cardIds },
      );
      continue;
    }

    if (saveForUndo) {
      for (const cardId of cardIds) {
        markUndoSnapshots.set(cardId, { docId, noteId, content: current });
      }
    }

    const ids = new Set(cardIds);
    updateNotesContent(
      docId,
      (live) => {
        // The marks come out of THIS document, so this is the one an undo
        // should look at — the peek above may predate edits made while the
        // cleanup waited for an editor. Only for a card whose mark is still
        // here (a sweep on another device may have taken it already, and the
        // peek is then the last copy that knows where it was) and whose
        // snapshot is still wanted (an undo that already ran took its entry,
        // and must not be handed a new one to leak).
        if (saveForUndo) {
          const present = collectFlashcardMarkIdsFromJSON(live);
          for (const cardId of cardIds) {
            if (present.has(cardId) && markUndoSnapshots.has(cardId)) {
              markUndoSnapshots.set(cardId, { docId, noteId, content: live });
            }
          }
        }
        const cleaned = removeFlashcardMarksFromJSON(live, ids);
        return cleaned === live ? null : cleaned;
      },
      { noteId },
    );
  }
}

/**
 * Restore flashcard marks in notes documents for un-deleted cards.
 * Uses undo snapshots saved by cleanupNotesMarks — for WHERE the marks were,
 * never for what the text was: a mark comes back only onto text that still
 * reads the same, and text the user has since changed is left as it is.
 * Fire-and-forget — callers should NOT await this.
 */
export async function restoreNotesMarks(cardIds: string[]): Promise<void> {
  const byDoc = new Map<string, { ids: string[]; snapshot: JSONContent }>();

  for (const cardId of cardIds) {
    const snap = markUndoSnapshots.get(cardId);
    if (!snap) continue;

    const key = groupKey(snap.docId, snap.noteId);
    const existing = byDoc.get(key);
    if (existing) {
      existing.ids.push(cardId);
    } else {
      byDoc.set(key, { ids: [cardId], snapshot: snap.content });
    }
    markUndoSnapshots.delete(cardId);
  }

  if (byDoc.size === 0) return;

  for (const [key, { ids, snapshot }] of byDoc) {
    const { docId, noteId } = splitGroupKey(key);
    const current = readNotesJSON(docId, noteId);
    if (!current) continue;
    const wanted = new Set(ids);
    updateNotesContent(
      docId,
      (live) => {
        const restored = addFlashcardMarksFromSnapshot(live, snapshot, wanted);
        return restored === live ? null : restored;
      },
      { noteId },
    );
    emitNotesMarksRestored({ docId });
  }
}

/** Clear undo snapshot for a card (used on hard-delete / purge). */
export function clearUndoSnapshot(cardId: string): void {
  markUndoSnapshots.delete(cardId);
}
